import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useRouter } from "@tanstack/react-router";
import { PATHS } from "@/config/paths";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNotification } from "@/hooks/useNotification";
import { resetPassword } from "@/lib/auth-client";
import { useSearchParam } from "@/hooks/useSearchParam";

export const Route = createFileRoute("/_home/resetPassword")({
  component: ResetPasswordPage,
});

const resetPasswordFormSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters long"),
});

type ResetPasswordValues = z.infer<typeof resetPasswordFormSchema>;

function ResetPasswordPage() {
  const router = useRouter();
  const { showNotification } = useNotification();
  const [token] = useSearchParam("token");

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      password: "",
    },
  });

  const onSubmit = async (values: ResetPasswordValues) => {
    if (!token) {
      showNotification({
        type: "error",
        message: "Error",
        description: "Invalid or expired reset token. Please request a new password reset.",
      });
      return;
    }

    const { error } = await resetPassword({
      token,
      newPassword: values.password,
    });

    if (error) {
      showNotification({
        type: "error",
        message: "Error",
        description: "Failed to reset password. Please try again or request a new reset link.",
      });
      return;
    }

    showNotification({
      type: "success",
      message: "Success",
      description: "Your password has been changed. You are now logged in.",
    });

    router.navigate({ to: PATHS.DASHBOARD });
  };

  return (
    <Card className="col-span-12 sm:col-span-10 sm:col-start-2 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
      <CardHeader>
        <CardTitle asHeading>Reset Password</CardTitle>
        <CardDescription>Enter your new password below.</CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="****"
                      type="password"
                      required
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button data-umami-event="reset-password-form-submit" type="submit" className="w-full">
              Reset Password
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
